<template>
  <el-card class="task-card" :class="{ 'is-completed': task.progressStatus === 1 }">
    <template #header>
      <div class="card-header">
        <div class="task-info">
          <span class="task-title">任务 {{ task.orderNum }}: {{ task.title }}</span>
          <el-tag 
            :type="task.progressStatus === 1 ? 'success' : 'info'" 
            size="small"
            class="status-tag"
          >
            {{ task.progressStatus === 1 ? '已完成' : '未完成' }}
          </el-tag>
        </div>
        <div class="task-meta">
          <el-tag size="small" type="info" v-if="task.labels">{{ task.labels }}</el-tag>
        </div>
      </div>
    </template>

    <div class="task-content">
      <p class="task-description">{{ task.description }}</p>
      
      <div class="task-progress">
      
        <div class="release-time-info" v-if="task.status==2" >
          <el-tag size="small" :type="task.status == 1 ? 'danger' : 'success'">
            <el-icon><Cloudy /></el-icon>
            状态: {{ task.status == 1 ? '未发布' : '已发布' }}
          </el-tag>
          <el-tag size="small" type="info" v-if="task.releaseTime">
            <el-icon><Clock /></el-icon>
            发布时间: {{ formatReleaseTime(task.releaseTime) }}
          </el-tag>
          <el-tag size="small" type="success" class="practice-duration" v-if="isReleaseTimePassed(task.releaseTime)">
            <el-icon><Timer /></el-icon>
            已练习: {{ calculatePracticeDuration(task.releaseTime) }}
          </el-tag>
        </div>
        <el-progress 
          :percentage="calculatedProgress"
          :status="calculatedProgress === 100 ? 'success' : ''"
          :stroke-width="15"
        />
      </div>

      <div class="task-stats">
        <el-tooltip content="已完成人数/总人数" placement="top">
          <div class="stat-item">
            <el-icon><User /></el-icon>
            <span>{{ task.completeNum }}/{{ task.totalNum }}</span>
          </div>
        </el-tooltip>
      </div>

      <div class="task-actions">
        <!-- 学生操作：在用户存在且角色不是管理员时显示，或用户不存在时显示 -->
        <template v-if="!user || user.role !== 2">
          <el-button 
            type="primary" 
            @click="handleMarkComplete"
          >
            <el-icon><Check /></el-icon>
            {{ isLogin && task.progressStatus === 1 ? '编辑' : '标记完成' }}
          </el-button>
        </template>

        <!-- 管理员操作：仅在用户存在且角色是管理员时显示 -->
        <template v-if="user && user.role === 2">
          <el-button-group>
            <el-button type="primary" @click="handleEditTask">
              <el-icon><Edit /></el-icon>编辑
            </el-button>
            <el-button type="danger" @click="handleDeleteTask">
              <el-icon><Delete /></el-icon>删除
            </el-button>
          </el-button-group>
        </template>

        <!-- 所有用户都可以查看 -->
        <el-button @click="handleViewStatus">
          <el-icon><View /></el-icon>查看详情
        </el-button>
      </div>
    </div>
  </el-card>

  <!-- 标记完成弹窗 -->
  <el-dialog 
    :title="isLogin && task.progressStatus === 1 ? '编辑任务完成情况(重复提交会覆盖之前的截图)' : (task.submission ? '重新提交任务' : '标记任务完成')" 
    v-model="showMarkCompleteDialog" 
    width="500px"
    :close-on-click-modal="false"
    :fullscreen="isMobile"
  >
    <div class="dialog-task-title">任务标题: {{ task.title }}</div>
    <el-form 
      ref="submitFormRef"
      :model="submitForm"
      :rules="submitRules"
      label-width="80px"
    >
    
      <el-form-item label="提交者">
        <el-input :model-value="user?.username" disabled />
      </el-form-item>
      <el-form-item label="专业">
        <el-input :model-value="user?.subject" disabled />
      </el-form-item>

      <el-form-item label="截图" prop="screenshot">
        <el-upload
          ref="uploadRef"
          action="http://tuku.yangeit.cn:21012/api/index.php"
          :headers="uploadHeaders"
          :auto-upload="true"
          :limit="1"
          accept="image/*"
          :on-exceed="handleExceed"
          :on-success="handleUploadSuccess"
          :on-error="handleUploadError"
          :on-remove="handleRemoveUpload"
          :file-list="fileList"
          :data="{ token: 'd3e42cb544a656b7441a82aa5a414bf4'}"
          name="image"
          list-type="picture-card"
        >
          <el-icon><Plus /></el-icon>
          <template #tip>
            <div class="el-upload__tip">
              只能上传1张 jpg/png文件，且不超过5MB
            </div>
          </template>
        </el-upload>
      </el-form-item>

    </el-form>

    <template #footer>
      <span class="dialog-footer">
        <el-button @click="showMarkCompleteDialog = false">取消</el-button>
        <el-button 
          type="primary" 
          @click="submitTaskWithScreenshot"
          :loading="submitting"
        >
          提交
        </el-button>
      </span>
    </template>
  </el-dialog>

  <!-- 查看完成情况弹窗 -->
  <el-dialog 
    title="任务完成情况" 
    v-model="showViewStatusDialog" 
    width="800px"
    :close-on-click-modal="false"
    :fullscreen="isMobile"
  >
    <div v-loading="loadingSubmissions">
      <div v-if="taskDetail">
        <el-descriptions :column="isMobile ? 1 : 2" border title="任务详情">
          <el-descriptions-item label="任务名称">{{ taskDetail.title }}</el-descriptions-item>
          <el-descriptions-item label="所属部分">{{ taskDetail.part }}</el-descriptions-item>
          <el-descriptions-item label="标签" :span="isMobile ? 1 : 2">{{ taskDetail.labels || '无' }}</el-descriptions-item>
          <el-descriptions-item label="任务描述" :span="isMobile ? 1 : 2">{{ taskDetail.description || '无' }}</el-descriptions-item>
          <el-descriptions-item label="发布时间">{{ taskDetail.releaseTime || '未设置' }}</el-descriptions-item>
          <el-descriptions-item label="总人数">{{ taskDetail.totalNum }}</el-descriptions-item>
          <el-descriptions-item label="完成人数">{{ taskDetail.completeNum }}</el-descriptions-item>
        </el-descriptions>

      
        <!-- 完成用户列表 -->
        <div>
          <h4>完成用户 ({{ taskDetail.completeds ? taskDetail.completeds.length : 0 }}人):</h4>
          <div class="user-list completed-users-text">
            <span v-if="taskDetail.completeds && taskDetail.completeds.length > 0">
              <span 
                v-for="(user, index) in taskDetail.completeds" 
                :key="user.userId" 
                class="completed-user-text user-text-item clickable-username"
                @click="handleUserClick(user.userId)"
              >
                {{ user.username }}{{ index < taskDetail.completeds.length - 1 ? '，' : '' }}
              </span>
            </span>
            <span v-else>暂无完成用户</span>
          </div>
        </div>

        <!-- 未完成用户列表 -->
        <div>
          <h4>未完成用户 ({{ taskDetail.uncompleteds ? taskDetail.uncompleteds.length : 0 }}人):</h4>
           <div class="user-list uncompleted-users-text">
            <span v-if="taskDetail.uncompleteds && taskDetail.uncompleteds.length > 0">
              <span 
                v-for="(user, index) in taskDetail.uncompleteds" 
                :key="user.userId" 
                class="uncompleted-user-text user-text-item clickable-username"
                @click="handleUserClick(user.userId)"
              >
                 {{ user.username }}{{ index < taskDetail.uncompleteds.length - 1 ? '，' : '' }}
              </span>
            </span>
             <span v-else>暂无未完成用户</span>
          </div>
        </div>

        <!-- 管理员视图下的提交列表（如果还需要） -->
        <!-- 您可以根据需要决定是否保留或修改这部分逻辑 -->
       

      </div>
      <el-empty v-else description="无法加载任务详情" />
    </div>
  </el-dialog>

  <!-- 编辑任务弹窗 -->
  <el-dialog
    title="编辑任务"
    v-model="showEditTaskDialog"
    width="500px"
    :close-on-click-modal="false"
    :fullscreen="isMobile"
  >
    <el-form
      ref="editFormRef"
      :model="editForm"
      :rules="editRules"
      label-width="80px"
    >
      <el-form-item label="标题" prop="title">
        <el-input v-model="editForm.title" />
      </el-form-item>
      <el-form-item label="描述" prop="description">
        <el-input
          v-model="editForm.description"
          type="textarea"
          :rows="3"
        />
      </el-form-item>
      <el-form-item label="标签" prop="labels">
        <el-input v-model="editForm.labels" placeholder="多个标签用逗号分隔" />
      </el-form-item>
      <el-form-item label="部分" prop="part">
        <el-input v-model="editForm.part" placeholder="例如：day01" />
      </el-form-item>
      <el-form-item label="序号" prop="orderNum">
        <el-input-number v-model="editForm.orderNum" :min="1" :max="999" />
      </el-form-item>
      <el-form-item label="类型" prop="type">
        <el-input v-model="editForm.type"  />
      </el-form-item>
      <el-form-item label="发布时间" prop="releaseTime">
        <el-date-picker
          v-model="editForm.releaseTime"
          type="datetime"
          placeholder="选择发布时间"
          format="YYYY-MM-DD HH:mm:ss"
          value-format="YYYY-MM-DD HH:mm:ss"
          style="width: 100%"
        />
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-select v-model="editForm.status" placeholder="请选择状态" style="width: 100%">
          <el-option label="未发布" :value="1" />
          <el-option label="已发布" :value="2" />
        </el-select>
      </el-form-item>
    </el-form>
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="showEditTaskDialog = false">取消</el-button>
        <el-button type="primary" @click="submitEditTask" :loading="editing">
          保存
        </el-button>
      </span>
    </template>
  </el-dialog>

  <!-- 删除任务确认对话框 -->
  <el-dialog
    v-model="showDeleteConfirmDialog"
    title="删除任务"
    width="400px"
    :close-on-click-modal="false"
    :fullscreen="isMobile"
    class="delete-confirm-dialog"
  >
    <div class="delete-confirm-content">
      <el-icon class="warning-icon"><Warning /></el-icon>
      <p>确定要删除任务 "{{ task.title }}" 吗？</p>
      <p class="warning-text">此操作不可恢复，请谨慎操作！</p>
    </div>
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="showDeleteConfirmDialog = false">取消</el-button>
        <el-button type="danger" @click="confirmDeleteTask" :loading="deleting">
          确定删除
        </el-button>
      </span>
    </template>
  </el-dialog>

  <!-- 添加用户信息侧边栏组件 -->
  <user-profile-drawer
    v-model="showUserProfile"
    :user-id="selectedUserId"
    @show-login="handleRequestLogin"
  />
</template>

<script>
import { ref, computed, toRefs, onMounted, onUnmounted } from 'vue';
import { 
  Check, 
  View, 
  Edit, 
  Delete, 
  User, 
  Plus,
  Warning,
  Clock,
  Timer,
  Cloudy
} from '@element-plus/icons-vue';
import taskApi from '../api/task';
import { ElMessage } from 'element-plus';
import { useStore } from 'vuex';
import UserProfileDrawer from './UserProfileDrawer.vue';

export default {
  components: {
    Check,
    View,
    Edit,
    Delete,
    User,
    Plus,
    Warning,
    UserProfileDrawer,
    Clock,
    Timer,
    Cloudy
  },
  props: {
    task: {
      type: Object,
      required: true,
    },
  },
  emits: ['refresh', 'requestLogin'],
  setup(props, { emit }) {
    const { task } = toRefs(props);
    const store = useStore();
    const user = computed(() => store.getters.userInfo);
    const isLogin = computed(() => store.getters.isLogin);

    // 状态变量
    const showMarkCompleteDialog = ref(false);
    const showViewStatusDialog = ref(false);
    const showEditTaskDialog = ref(false);
    const submitting = ref(false);
    const editing = ref(false);
    const loadingSubmissions = ref(false);
    const uploadRef = ref(null);
    const fileList = ref([]);
    const submissions = ref([]);
    const submissionFilter = ref({
      status: null,
      username: '',
    });
    const taskDetail = ref(null);
    const showDeleteConfirmDialog = ref(false);
    const deleting = ref(false);

    // 表单相关
    const submitFormRef = ref(null);
    const editFormRef = ref(null);
    const submitForm = ref({
      screenshot: '',
    });
    const editForm = ref({
      title: '',
      description: '',
      labels: '',
      part: '',
      orderNum: 1,
      type: '',
      releaseTime: '',
      status: 1
    });

    // 分页相关
    const submissionPagination = ref({
      page: 1,
      limit: 10,
      total: 0
    });

    // 上传相关
    const uploadHeaders = computed(() => {
      const token = localStorage.getItem('token');
      return token ? { Authorization: `Bearer ${token}` } : {};
    });

    // 表单验证规则
    const submitRules = {
      screenshot: [
        { required: true, message: '请上传任务截图', trigger: 'change' }
      ]
    };

    const editRules = {
      title: [
        { required: true, message: '请输入任务标题', trigger: 'blur' },
        { max: 255, message: '标题不能超过255个字符', trigger: 'blur' }
      ],
      description: [
        { required: true, message: '请输入任务描述', trigger: 'blur' }
      ],
      part: [
        { required: true, message: '请输入任务部分', trigger: 'blur' }
      ],
      orderNum: [
        { required: true, message: '请输入任务序号', trigger: 'blur' }
      ],
      type: [
        { required: true, message: '请输入任务类型', trigger: 'blur' }
      ],
      releaseTime: [
        { required: true, message: '请选择发布时间', trigger: 'change' }
      ],
      status: [
        { required: true, message: '请选择状态', trigger: 'change' }
      ]
    };

    // 计算属性
    const filteredSubmissions = computed(() => {
      let result = [...submissions.value];
      if (submissionFilter.value.status) {
        result = result.filter(s => s.review_status === submissionFilter.value.status);
      }
      if (submissionFilter.value.username) {
        const username = submissionFilter.value.username.toLowerCase();
        result = result.filter(s => 
          s.user.username.toLowerCase().includes(username)
        );
      }
      return result;
    });

    const calculatedProgress = computed(() => {
      if (task.value.totalNum === 0) {
        return 0;
      }
      return Math.round((task.value.completeNum / task.value.totalNum) * 100);
    });

    // 工具函数
    const getStatusType = (status) => {
      switch (status) {
        case 1: return 'warning';
        case 2: return 'success';
        case 3: return 'danger';
        default: return 'info';
      }
    };

    const getStatusText = (status) => {
      switch (status) {
        case 1: return '待审核';
        case 2: return '已通过';
        case 3: return '未通过';
        default: return '未完成';
      }
    };

    const formatDate = (date) => {
      if (!date) return '';
      return new Date(date).toLocaleString();
    };

    const formatReleaseTime = (releaseTime) => {
      if (!releaseTime) return '';
      return formatDate(releaseTime);
    };

    const calculatePracticeDuration = (releaseTime) => {
      if (!releaseTime) return '';
      const releaseDateTime = new Date(releaseTime).getTime();
      const now = currentTime.value.getTime();
      const duration = Math.floor((now - releaseDateTime) / 1000);
      
      if (duration < 0) return '0分钟';
      
      const hours = Math.floor(duration / 3600);
      const minutes = Math.floor((duration % 3600) / 60);
      
      if (hours > 0) {
        return `${hours}小时${minutes}分钟`;
      } else if (minutes > 0) {
        return `${minutes}分钟`;
      } else {
        return '1分钟';
      }
    };

    const isReleaseTimePassed = (releaseTime) => {
      if (!releaseTime) return false;
      const releaseDateTime = new Date(releaseTime).getTime();
      const now = currentTime.value.getTime();
      return now >= releaseDateTime;
    };

    // 事件处理函数
    const handleMarkComplete = () => {
      if (!isLogin.value) {
        emit('requestLogin');
      } else {
        if (task.value.progressStatus === 1) {
          if (task.value.screenshot) {
            fileList.value = [{
              name: '已上传的截图',
              url: task.value.screenshot
            }];
          } else {
            fileList.value = [];
          }
        } else {
          fileList.value = [];
        }
        showMarkCompleteDialog.value = true;
      }
    };

    const handleViewStatus = async () => {
      if (!task.value || !task.value.taskId) {
        ElMessage.warning('无法获取任务 ID，请稍后再试');
        return;
      }

      loadingSubmissions.value = true;
      taskDetail.value = null;

      try {
        const response = await taskApi.getTaskInfo(task.value.taskId);
        if (response.code === '000000') {
          taskDetail.value = response.data;
          showViewStatusDialog.value = true;
        } else {
          ElMessage.error('获取任务详情失败: ' + (response.msg || '未知错误'));
        }
      } catch (error) {
        ElMessage.error('获取任务详情异常: ' + error.message);
      } finally {
        loadingSubmissions.value = false;
      }
    };

    const handleEditTask = () => {
      editForm.value = {
        title: task.value.title,
        description: task.value.description,
        labels: task.value.labels || '',
        part: task.value.part || '',
        orderNum: task.value.orderNum || 1,
        type: task.value.type || '',
        releaseTime: task.value.releaseTime || '',
        status: task.value.status || 1
      };
      showEditTaskDialog.value = true;
    };

    const handleDeleteTask = () => {
      showDeleteConfirmDialog.value = true;
    };

    const confirmDeleteTask = async () => {
      try {
        deleting.value = true;
        const response = await taskApi.deleteTask(task.value.taskId);
        if (response.code === '000000') {
          ElMessage.success('任务删除成功');
          showDeleteConfirmDialog.value = false;
          emit('refresh');
        } else {
          throw new Error(response.msg || '删除失败');
        }
      } catch (error) {
        ElMessage.error('删除任务失败: ' + error.message);
      } finally {
        deleting.value = false;
      }
    };

    const handleExceed = () => {
      ElMessage.warning('只能上传一个文件');
    };

    const handleUploadSuccess = (response) => {
      if (response.result === 'success' && response.code === 200) {
        ElMessage.success('文件上传成功');
        submitForm.value.screenshot = response.url;
      } else {
        ElMessage.error('文件上传失败: ' + (response.msg || '未知错误'));
        uploadRef.value.clearFiles();
        submitForm.value.screenshot = '';
      }
    };

    const handleUploadError = (error) => {
      console.error('上传错误:', error);
      ElMessage.error('文件上传失败，请重试');
      uploadRef.value.clearFiles();
    };

    const handleRemoveUpload = () => {
      submitForm.value.screenshot = '';
    };

    const submitTaskWithScreenshot = async () => {
      if (!submitFormRef.value) return;
      
      await submitFormRef.value.validate(async (valid) => {
        if (valid) {
          submitting.value = true;
          try {
            if (!submitForm.value.screenshot) {
              ElMessage.warning('请上传任务截图');
              return;
            }

            await submitTask(submitForm.value.screenshot);

          } catch (error) {
            // ElMessage.error('提交失败: ' + error.message);
          } finally {
            submitting.value = false;
          }
        }
      });
    };

    const submitTask = async (screenshotPath) => {
      if (!user.value) {
        ElMessage.error('用户信息未加载，无法提交');
        return;
      }

      const data = {
        screenshot: screenshotPath,
        taskId: task.value.taskId,
        userId: user.value.userId,
        username: user.value.username,
      };

      console.log(data);
      
      const response = await taskApi.completeTask(data);

      if (response.code === '000000') {
        ElMessage.success('任务提交成功');
        showMarkCompleteDialog.value = false;
        emit('refresh');
      } else {
        ElMessage.error('任务提交失败: ' + (response.msg || '未知错误'));
        throw new Error(response.msg || '任务提交失败');
      }
    };

    const submitEditTask = async () => {
      if (!editFormRef.value) return;

      await editFormRef.value.validate(async (valid) => {
        if (valid) {
          editing.value = true;
          try {
            const data = {
              ...editForm.value,
              taskId: task.value.taskId
            };
            const response = await taskApi.updateTask(data);
            if (response.code === '000000') {
              ElMessage.success('任务更新成功');
              showEditTaskDialog.value = false;
              emit('refresh');
            } else {
              throw new Error(response.msg || '更新失败');
            }
          } catch (error) {
            ElMessage.error('更新任务失败: ' + error.message);
          } finally {
            editing.value = false;
          }
        }
      });
    };

    const handleReview = async (userTaskId, status) => {
      try {
        await taskApi.reviewTask(userTaskId, { review_status: status });
        ElMessage.success('审核操作成功');
        await fetchSubmissions();
        emit('refresh');
      } catch (error) {
        ElMessage.error('审核操作失败: ' + error.message);
      }
    };

    const handleSubmissionPageSizeChange = (val) => {
      submissionPagination.value.limit = val;
      fetchSubmissions();
    };

    const handleSubmissionCurrentChange = (val) => {
      submissionPagination.value.page = val;
      fetchSubmissions();
    };

    // API 调用函数
    const fetchSubmissions = async () => {
      if (!user.value?.role === 2) return;
      
      loadingSubmissions.value = true;
      try {
        const response = await taskApi.getTaskSubmissions({
          taskId: task.value.taskId,
          page: submissionPagination.value.page,
          limit: submissionPagination.value.limit
        });
        submissions.value = response.data.submissions;
        submissionPagination.value.total = response.data.pagination.total;
      } catch (error) {
        ElMessage.error('获取提交列表失败: ' + error.message);
      } finally {
        loadingSubmissions.value = false;
      }
    };

    // 判断是否是移动端
    const isMobile = ref(false);
    const checkIsMobile = () => {
      isMobile.value = window.innerWidth < 768; // 假设 768px 以下为移动端
    };

    // 定时器相关
    const durationTimer = ref(null);
    const currentTime = ref(new Date());

    const startDurationTimer = () => {
      if (task.value.releaseTime && isReleaseTimePassed(task.value.releaseTime)) {
        durationTimer.value = setInterval(() => {
          currentTime.value = new Date();
        }, 1000); // 每秒更新一次
      }
    };

    const stopDurationTimer = () => {
      if (durationTimer.value) {
        clearInterval(durationTimer.value);
        durationTimer.value = null;
      }
    };

    onMounted(() => {
      checkIsMobile();
      window.addEventListener('resize', checkIsMobile);
      console.log('TaskCard mounted, user:', user.value);
      startDurationTimer();
    });

    onUnmounted(() => {
      window.removeEventListener('resize', checkIsMobile);
      stopDurationTimer();
    });

    const showUserProfile = ref(false);
    const selectedUserId = ref(null);

    const handleUserClick = (userId) => {
      if (!userId) {
        ElMessage.warning('用户ID无效');
        return;
      }
      selectedUserId.value = userId;
      showUserProfile.value = true;
    };

    const handleRequestLogin = () => {
      emit('requestLogin');
    };

    return {
      // 状态变量
      showMarkCompleteDialog,
      showViewStatusDialog,
      showEditTaskDialog,
      submitting,
      editing,
      loadingSubmissions,
      uploadRef,
      fileList,
      submissions,
      submissionFilter,
      submitFormRef,
      editFormRef,
      submitForm,
      editForm,
      submissionPagination,
      uploadHeaders,
      submitRules,
      editRules,
      filteredSubmissions,
      calculatedProgress,
      user,
      taskDetail,
      isLogin,
      isMobile,
      showUserProfile,
      selectedUserId,
      showDeleteConfirmDialog,
      deleting,

      // 方法
      getStatusType,
      getStatusText,
      formatDate,
      formatReleaseTime,
      calculatePracticeDuration,
      isReleaseTimePassed,
      handleMarkComplete,
      handleViewStatus,
      handleEditTask,
      handleDeleteTask,
      confirmDeleteTask,
      handleExceed,
      handleUploadSuccess,
      handleUploadError,
      handleRemoveUpload,
      submitTaskWithScreenshot,
      submitEditTask,
      handleReview,
      handleSubmissionPageSizeChange,
      handleSubmissionCurrentChange,
      handleUserClick,
      handleRequestLogin,
    };
  },
};
</script>

<style scoped>
.task-card {
  margin: 10px auto;
  max-width: 800px;
  width: 95%;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  transition: all 0.3s ease;
}

.task-card.is-completed {
  border-left: 5px solid var(--el-color-success);
  border-color: #ebeef5;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #ebeef5;
  flex-wrap: wrap;
}

.task-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-grow: 1;
  flex-basis: 0;
  min-width: 180px;
}

.task-title {
  font-size: 1.2em;
  font-weight: bold;
  color: var(--el-text-color-primary);
}

.status-tag {
  margin-left: 10px;
  flex-shrink: 0;
}

.task-meta {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.task-content {
  padding: 10px;
}

.task-description {
  margin: 0 0 20px 0;
  color: var(--el-text-color-regular);
  line-height: 1.6;
}

.task-progress {
  margin-bottom: 20px;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 0.9em;
  color: var(--el-text-color-secondary);
}

.release-time-info {
  margin-bottom: 8px;
  display: flex;
  justify-content: flex-start;
  gap: 8px;
  flex-wrap: wrap;
}

.release-time-info .el-tag {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.85em;
}

.practice-duration {
  background-color: var(--el-color-success-light-9) !important;
  border-color: var(--el-color-success-light-7) !important;
  color: var(--el-color-success) !important;
}

.task-stats {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--el-text-color-secondary);
  font-size: 0.9em;
}

.task-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  flex-wrap: wrap;
}

.submission-filters {
  margin-bottom: 15px;
  display: flex;
  gap: 10px;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.student-submission {
  padding: 20px;
}

.el-dialog__body .user-list {
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid #eee;
    padding: 15px;
    margin-bottom: 20px;
}

.user-list {
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid #eee;
    padding: 15px;
    margin-bottom: 20px;
}

.completed-users-text {
    /* 可以在这里添加特定样式，如果需要 */
}

.uncompleted-users-text {
     /* 可以在这里添加特定样式，如果需要 */
}

.completed-user-text {
    color: var(--el-color-success);
    margin-right: 5px;
}

.uncompleted-user-text {
    color: var(--el-color-danger);
     margin-right: 5px;
}

:deep(.el-upload--picture-card) {
  width: 120px;
  height: 120px;
  line-height: 120px;
}

:deep(.el-upload-list--picture-card .el-upload-list__item) {
  width: 120px;
  height: 120px;
}

.dialog-task-title {
    font-size: 1.1em;
    font-weight: bold;
    margin-bottom: 20px;
    color: var(--el-text-color-primary);
    padding: 0 20px;
}

.el-dialog__body .el-form {
    padding: 0 20px 20px;
}

.el-dialog__footer {
    padding: 15px 20px;
    border-top: 1px solid #ebeef5;
}

.screenshot-preview {
    max-width: 300px;
    max-height: 300px;
    overflow: hidden;
}

.screenshot-preview .el-image {
    display: block;
    max-width: 100%;
    height: auto;
}

@media (max-width: 768px) {
  .task-card {
    width: 98%;
    margin: 5px auto;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .task-info {
    margin-bottom: 10px;
  }

  .task-actions {
    justify-content: center;
    margin-top: 15px;
  }

  .el-dialog {
      width: 95% !important;
  }

  .el-dialog__body .el-form {
      padding: 0 10px 10px;
  }

  .el-dialog__footer {
      padding: 10px;
  }

  .dialog-task-title {
      padding: 0 10px;
      margin-bottom: 15px;
  }

   .user-list {
      padding: 10px;
      margin-bottom: 15px;
  }

   .screenshot-preview {
      max-width: 95vw;
      max-height: 60vh;
   }

  .release-time-info {
    margin-bottom: 6px;
  }

  .release-time-info .el-tag {
    font-size: 0.8em;
  }

  .practice-duration {
    font-size: 0.8em;
  }
}

.clickable-username {
  cursor: pointer;
  color: var(--el-color-primary);
  text-decoration: underline;
}

.clickable-username:hover {
  opacity: 0.8;
}

.completed-user-text .clickable-username {
  color: var(--el-color-success);
}

.uncompleted-user-text.clickable-username {
  color: var(--el-color-danger);
}

.delete-confirm-dialog :deep(.el-dialog__body) {
  padding: 20px;
}

.delete-confirm-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 20px 0;
}

.warning-icon {
  font-size: 48px;
  color: var(--el-color-danger);
  margin-bottom: 16px;
}

.warning-text {
  color: var(--el-color-danger);
  margin-top: 8px;
  font-size: 14px;
}

@media (max-width: 768px) {
  .delete-confirm-dialog :deep(.el-dialog__body) {
    padding: 15px;
  }

  .delete-confirm-content {
    padding: 15px 0;
  }

  .warning-icon {
    font-size: 36px;
    margin-bottom: 12px;
  }

  .el-form-item {
    margin-bottom: 15px;
  }

  .el-dialog__footer {
    padding: 10px;
  }

  :deep(.el-date-editor) {
    width: 100% !important;
  }
}
</style> 